// openEHR RM model — BMM package org.openehr.rm.data_types.encapsulated
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { DATA_VALUE } from "./basic.ts";
import type { CODE_PHRASE } from "./text.ts";
import type { DV_URI } from "./uri.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Abstract class defining the common meta-data of all types of encapsulated data.
 */
export abstract class DV_ENCAPSULATED extends DATA_VALUE {
  /**
   * Name of character encoding scheme in which this value is encoded. Coded from openEHR Code Set  character sets . Unicode is the default assumption in openEHR, with UTF-8 being the assumed encoding. This attribute allows for variations from these assumptions.
   */
  charset?: CODE_PHRASE;
  /**
   * Optional indicator of the localised language in which the data is written, if relevant. Coded from openEHR Code Set \`languages\`.
   */
  language?: CODE_PHRASE;
}

/**
 * A specialisation of \`DV_ENCAPSULATED\` for audiovisual and bio-signal types. Includes further metadata relating to multimedia types which are not applicable to other subtypes of \`DV_ENCAPSULATED\`.
 */
export class DV_MULTIMEDIA extends DV_ENCAPSULATED {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for alternate_text
   * @protected
   */
  protected _alternate_text?: openehr_base.String;

  /**
   * Text to display in lieu of multimedia display/replay.
   */
  get alternate_text(): string | undefined {
    return this._alternate_text?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for alternate_text.
   * Use this to access openehr_base.String methods.
   */
  get $alternate_text(): openehr_base.String | undefined {
    return this._alternate_text;
  }

  /**
   * Sets alternate_text from either a primitive value or openehr_base.String wrapper.
   */
  set alternate_text(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._alternate_text = undefined;
    } else if (typeof val === "string") {
      this._alternate_text = openehr_base.String.from(val);
    } else {
      this._alternate_text = val;
    }
  }

  /**
   * URI reference to electronic information stored outside the record as a file, database entry etc, if supplied as a reference.
   */
  uri?: DV_URI;
  /**
   * The actual data found at \`_uri_\`, if supplied inline.
   */
  data?: undefined;
  /**
   * Data media type coded from openEHR code set  media types  (interface for the IANA MIME types code set).
   */
  media_type?: CODE_PHRASE;
  /**
   * Compression type, a coded value from the openEHR Integrity check code set. Void means no compression.
   */
  compression_algorithm?: CODE_PHRASE;
  /**
   * Binary cryptographic integrity checksum.
   */
  integrity_check?: undefined;
  /**
   * Type of integrity check, a coded value from the openEHR \`Integrity check\` code set.
   */
  integrity_check_algorithm?: CODE_PHRASE;
  /**
   * The thumbnail for this item, if one exists; mainly for graphics formats.
   */
  thumbnail?: DV_MULTIMEDIA;
  /**
   * Internal storage for size
   * @protected
   */
  protected _size?: openehr_base.Integer;

  /**
   * Original size in bytes of unencoded encapsulated data. I.e. encodings such as base64, hexadecimal etc do not change the value of this attribute.
   */
  get size(): number | undefined {
    return this._size?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for size.
   * Use this to access openehr_base.Integer methods.
   */
  get $size(): openehr_base.Integer | undefined {
    return this._size;
  }

  /**
   * Sets size from either a primitive value or openehr_base.Integer wrapper.
   */
  set size(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._size = undefined;
    } else if (typeof val === "number") {
      this._size = openehr_base.Integer.from(val);
    } else {
      this._size = val;
    }
  }

  /**
   * Computed from the value of the \`_uri_\` attribute: True if  the data is stored externally to the record, as indicated by \`_uri_\`. A copy may also be stored internally, in which case \`_is_expanded_\` is also true.
   * @returns Result value
   */
  is_external(): openehr_base.Boolean {
    // External if uri is set
    return openehr_base.Boolean.from(this.uri !== undefined);
  }

  /**
   * Computed from the value of the data attribute. True if  the  data is stored  in  expanded  form, ie within the EHR itself.
   * @returns Result value
   */
  is_inline(): openehr_base.Boolean {
    // Inline if data is set
    return openehr_base.Boolean.from(this.data !== undefined);
  }

  /**
   * Computed from the value of the \`_compression_algorithm_\` attribute: True if  the  data is stored in compressed form.
   * @returns Result value
   */
  is_compressed(): openehr_base.Boolean {
    // Compressed if compression_algorithm is set
    return openehr_base.Boolean.from(this.compression_algorithm !== undefined);
  }

  /**
   * Computed from the value of the \`_integrity_check_algorithm_\` attribute: True if an integrity check has been computed.
   * @returns Result value
   */
  has_integrity_check(): openehr_base.Boolean {
    // Has integrity check if integrity_check_algorithm is set
    return openehr_base.Boolean.from(this.integrity_check_algorithm !== undefined);
  }
}

/**
 * Encapsulated data expressed as a parsable String. The internal model of the data item is not described in the openEHR model in common with other encapsulated types, but in this case, the form of the data is assumed to be plaintext, rather than compressed or other types of large binary data.
 */
export class DV_PARSABLE extends DV_ENCAPSULATED {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * The string, which may validly be empty in some syntaxes.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * Internal storage for formalism
   * @protected
   */
  protected _formalism?: openehr_base.String;

  /**
   * Name of the formalism, e.g.  GLIF 1.0 ,  Proforma  etc.
   */
  get formalism(): string | undefined {
    return this._formalism?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for formalism.
   * Use this to access openehr_base.String methods.
   */
  get $formalism(): openehr_base.String | undefined {
    return this._formalism;
  }

  /**
   * Sets formalism from either a primitive value or openehr_base.String wrapper.
   */
  set formalism(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._formalism = undefined;
    } else if (typeof val === "string") {
      this._formalism = openehr_base.String.from(val);
    } else {
      this._formalism = val;
    }
  }

  /**
   * Size in bytes of value.
   * @returns Result value
   */
  size(): openehr_base.Integer {
    // Calculate size based on value length
    const valueStr = this.value || "";
    // Assuming UTF-8 encoding, use TextEncoder to get actual byte length
    const encoder = new TextEncoder();
    const bytes = encoder.encode(valueStr);
    return openehr_base.Integer.from(bytes.length);
  }
}
